import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import PropTypes from 'prop-types';
import { io, Socket } from 'socket.io-client';
import AuthContext from './authContext';
import {
	getLiveReflectionTokenName,
	LiveReflectionChannel,
	LiveReflectionEvent,
	LiveReflectionHandler,
	LiveReflectionPayload,
	LiveReflectionSocketEvent,
	LiveReflectionSocketPayload,
	LiveReflectionToken,
	LiveReflectionUserType,
} from '../liveReflections';

type LiveReflectionStatus = 'disabled' | 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';
type LiveReflectionScope = 'client' | 'dealer' | null;

interface ILiveReflectionContextProps {
	status: LiveReflectionStatus;
	isConnected: boolean;
	socketId: string | null;
	scope: LiveReflectionScope;
	channel: LiveReflectionChannel | null;
	lastEvent: LiveReflectionEvent | null;
	receivedCount: number;
	subscribe: (handler: LiveReflectionHandler) => () => void;
	subscribeToToken: (
		tokens: LiveReflectionToken | number | Array<LiveReflectionToken | number>,
		handler: LiveReflectionHandler,
	) => () => void;
}

const LiveReflectionContext = createContext<ILiveReflectionContextProps>({
	status: 'disabled',
	isConnected: false,
	socketId: null,
	scope: null,
	channel: null,
	lastEvent: null,
	receivedCount: 0,
	subscribe: () => () => undefined,
	subscribeToToken: () => () => undefined,
});

interface ILiveReflectionContextProviderProps {
	children: ReactNode;
}

const LOG_PREFIX = '[LiveReflection]';
const liveReflectionBackendUrl = process.env.REACT_APP_LIVE_REFLECTION_BACKEND_URL?.trim();

const getReflectionConnection = (userTypeId?: number): {
	scope: LiveReflectionScope;
	channel: LiveReflectionChannel | null;
} => {
	if (userTypeId === LiveReflectionUserType.Client || userTypeId === LiveReflectionUserType.Office) {
		return {
			scope: 'client',
			channel: LiveReflectionChannel.ClientsReflection,
		};
	}

	if (userTypeId === LiveReflectionUserType.Dealer) {
		return {
			scope: 'dealer',
			channel: LiveReflectionChannel.DealersReflection,
		};
	}

	return {
		scope: null,
		channel: null,
	};
};

const toNumber = (value: unknown) => {
	if (value === undefined || value === null || value === '') {
		return undefined;
	}

	const parsedValue = Number(value);
	return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const parseReflectionEvent = (
	socketPayload: LiveReflectionSocketPayload,
	fallbackChannel: LiveReflectionChannel,
): LiveReflectionEvent => {
	const rawMessage = socketPayload?.message ?? socketPayload;
	let payload: LiveReflectionPayload | undefined;

	if (typeof rawMessage === 'string') {
		try {
			payload = JSON.parse(rawMessage);
		} catch {
			// Keep malformed reflection payloads out of the browser console.
		}
	} else if (rawMessage && typeof rawMessage === 'object') {
		payload = rawMessage as LiveReflectionPayload;
	}

	const token = toNumber(payload?.FUNCTION_TOKEN);
	const affectedId = toNumber(payload?.AFFECTED_ID);

	return {
		channel: socketPayload?.event_name || fallbackChannel,
		token,
		tokenName: getLiveReflectionTokenName(token),
		affectedId,
		data: payload?.data,
		payload,
		rawMessage,
		socketPayload,
		receivedAt: new Date().toISOString(),
	};
};

const logReflectionEvent = (event: LiveReflectionEvent) => {
	console.info(`${LOG_PREFIX} Event received.`, {
		channel: event.channel,
		token: event.token,
		tokenName: event.tokenName,
		affectedId: event.affectedId,
		receivedAt: event.receivedAt,
	});
};

export const LiveReflectionContextProvider: FC<ILiveReflectionContextProviderProps> = ({ children }) => {
	const { userData } = useContext(AuthContext);
	const socketRef = useRef<Socket | null>(null);
	const logOnceRef = useRef<Set<string>>(new Set());
	const subscribersRef = useRef<Set<LiveReflectionHandler>>(new Set());
	const tokenSubscribersRef = useRef<Map<number, Set<LiveReflectionHandler>>>(new Map());
	const [status, setStatus] = useState<LiveReflectionStatus>('disabled');
	const [socketId, setSocketId] = useState<string | null>(null);
	const [scope, setScope] = useState<LiveReflectionScope>(null);
	const [channel, setChannel] = useState<LiveReflectionChannel | null>(null);
	const [lastEvent, setLastEvent] = useState<LiveReflectionEvent | null>(null);
	const [receivedCount, setReceivedCount] = useState(0);

	const logOnce = useCallback((key: string, message: string, data?: unknown) => {
		if (logOnceRef.current.has(key)) {
			return;
		}

		logOnceRef.current.add(key);
		if (data !== undefined) {
			console.info(message, data);
		} else {
			console.info(message);
		}
	}, []);

	const publishToSubscribers = useCallback((event: LiveReflectionEvent) => {
		subscribersRef.current.forEach((handler) => handler(event));

		if (event.token !== undefined) {
			const tokenHandlers = tokenSubscribersRef.current.get(Number(event.token));
			tokenHandlers?.forEach((handler) => handler(event));
		}
	}, []);

	const subscribe = useCallback((handler: LiveReflectionHandler) => {
		subscribersRef.current.add(handler);

		return () => {
			subscribersRef.current.delete(handler);
		};
	}, []);

	const subscribeToToken = useCallback(
		(
			tokens: LiveReflectionToken | number | Array<LiveReflectionToken | number>,
			handler: LiveReflectionHandler,
		) => {
			const tokenList = Array.isArray(tokens) ? tokens : [tokens];

			tokenList.forEach((token) => {
				const numericToken = Number(token);
				const handlers = tokenSubscribersRef.current.get(numericToken) || new Set();
				handlers.add(handler);
				tokenSubscribersRef.current.set(numericToken, handlers);
			});

			return () => {
				tokenList.forEach((token) => {
					const numericToken = Number(token);
					const handlers = tokenSubscribersRef.current.get(numericToken);
					handlers?.delete(handler);
					if (handlers?.size === 0) {
						tokenSubscribersRef.current.delete(numericToken);
					}
				});
			};
		},
		[],
	);

	useEffect(() => {
		const token = userData.token || localStorage.getItem('ark_authToken') || '';
		const userTypeId = toNumber(userData.userTypeId);

		if (socketRef.current) {
			socketRef.current.disconnect();
			socketRef.current = null;
			setSocketId(null);
			setScope(null);
			setChannel(null);
		}

		if (!liveReflectionBackendUrl) {
			setStatus('disabled');
			setScope(null);
			setChannel(null);
			logOnce(
				'missing-url',
				`${LOG_PREFIX} Disabled. Set REACT_APP_LIVE_REFLECTION_BACKEND_URL to enable CRM live reflections.`,
			);
			return undefined;
		}

		if (!token) {
			setStatus('idle');
			setScope(null);
			setChannel(null);
			logOnce('missing-token', `${LOG_PREFIX} Waiting for login token before opening socket.`);
			return undefined;
		}

		const connection = getReflectionConnection(userTypeId);

		if (!connection.scope || !connection.channel) {
			setStatus('idle');
			setScope(null);
			setChannel(null);
			if (userTypeId) {
				logOnce(
					`unsupported-user-type-${userTypeId}`,
					`${LOG_PREFIX} Live reflection socket skipped for unsupported login type.`,
					{ userTypeId },
				);
			}
			return undefined;
		}

		const reflectionScope = connection.scope;
		const reflectionChannel = connection.channel;

		setScope(reflectionScope);
		setChannel(reflectionChannel);
		setStatus('connecting');
		// console.info(
		// 	`${LOG_PREFIX} Connecting to ${reflectionScope === 'dealer' ? 'dealer' : 'client'} reflections.`,
		// 	{
		// 		scope: reflectionScope,
		// 		channel: reflectionChannel,
		// 	},
		// );

		const auth: Record<string, string | boolean> = {
			Authorization: token,
		};
		if (reflectionScope === 'dealer') {
			auth.reflectionScope = 'dealer';
			auth.clientType = 'dealer';
			auth.dealerReflection = true;
		}

		const socket = io(liveReflectionBackendUrl, {
			transports: ['polling'],
			reconnection: true,
			auth,
		});

		socketRef.current = socket;

		socket.on('connect', () => {
			setStatus('connected');
			setSocketId(socket.id || null);
			console.info(`${LOG_PREFIX} Connected.`, {
				scope: reflectionScope,
				channel: reflectionChannel,
			});

			if (reflectionScope === 'dealer') {
				socket.emit('subscribe_dealers_reflection');
			}
		});

		socket.on(LiveReflectionSocketEvent.Client, (payload: LiveReflectionSocketPayload) => {
			const event = parseReflectionEvent(payload, reflectionChannel);
			logReflectionEvent(event);
			setLastEvent(event);
			setReceivedCount((count) => count + 1);

			publishToSubscribers(event);
		});

		socket.on('connect_error', (error) => {
			setStatus('error');
			console.error(`${LOG_PREFIX} Connection error.`, error.message);
		});

		socket.on('disconnect', (reason) => {
			setStatus('disconnected');
			setSocketId(null);
			console.warn(`${LOG_PREFIX} Disconnected.`, { reason, scope: reflectionScope });
		});

		return () => {
			console.info(`${LOG_PREFIX} Closing live reflection socket.`, {
				scope: reflectionScope,
				channel: reflectionChannel,
			});
			socket.off();
			socket.disconnect();
			if (socketRef.current === socket) {
				socketRef.current = null;
			}
		};
	}, [logOnce, publishToSubscribers, userData.id, userData.token, userData.userTypeId]);

	const value = useMemo(
		() => ({
			status,
			isConnected: status === 'connected',
			socketId,
			scope,
			channel,
			lastEvent,
			receivedCount,
			subscribe,
			subscribeToToken,
		}),
		[channel, lastEvent, receivedCount, scope, socketId, status, subscribe, subscribeToToken],
	);

	return <LiveReflectionContext.Provider value={value}>{children}</LiveReflectionContext.Provider>;
};

LiveReflectionContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useLiveReflections = () => useContext(LiveReflectionContext);

export default LiveReflectionContext;
